import { useState, useEffect } from 'react';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../../util/toasts';
import { FuelChargingConfig, Product } from '../../../../clients/quipli-service/product/product';
import Select, { SingleValue } from 'react-select';
import { Tooltip } from '../../../../components/tooltip/tooltip';
import Icon from '../../../../components/icons/icon';
import { usePricingApiV1 } from '../../../../clients/quipli-service/pricing/pricing-api-v1';
import { OrderType } from '../../../../clients/quipli-service/order/order';
import { Typography } from '../../../../components/typography/typography';

export const FuelChargingConfigForm = ({ productId }: { productId: number }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [fuelProduct, setFuelProduct] = useState<Product>();
  const [fuelConfig, setFuelConfig] = useState<FuelChargingConfig>();
  const [validFuelProduct, setValidFuelProduct] = useState<boolean>(true);

  const [isLoading, setIsLoading] = useState(true);
  const { listProducts, getFuelChargingConfig, updateFuelChargingConfig, deleteFuelChargingConfig } = useAPI();
  const { listPrices } = usePricingApiV1();

  const onProductChange = async (product: SingleValue<Product>) => {
    setFuelProduct(product as Product);

    try {
      setIsLoading(true);

      // If clearing, we'll just delete the entry
      if (!product) {
        await deleteFuelChargingConfig(productId);
        setFuelConfig(undefined);
      } else {
        const newConfig = await updateFuelChargingConfig({ productId, fuelChargeProductId: product?.id });
        setFuelConfig(newConfig);
      }

      toast('Fuel Product updated successfully');
    } catch {
      toast('Failed to update Fuel Product', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Update the selected product when the fuelConfig changes
  useEffect(() => {
    const newFuelConfigSelected = async () => {
      if (fuelConfig) {
        const newFuelProduct = products.find((p) => p.id === fuelConfig.fuelChargeProductId);
        setFuelProduct(newFuelProduct);

        const productPrice = await listPrices({
          productIds: newFuelProduct?.id ? [newFuelProduct?.id] : [],
          type: OrderType.Sale,
        });

        if (newFuelProduct && productPrice.length === 0) {
          setValidFuelProduct(false);
        } else {
          setValidFuelProduct(true);
        }
      }
    };
    newFuelConfigSelected();
  }, [fuelConfig]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        setIsLoading(true);
        const products = await listProducts();
        let fuelConfig = null;

        if (productId) {
          fuelConfig = await getFuelChargingConfig(productId);
          setFuelConfig(fuelConfig);
        }
        setProducts(products);
      } catch {
        toast('Failed to load Fuel Products', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, [productId]);

  const productsToOptions = (p: Product) => p.name;

  return (
    <div className="fuel-charging-config">
      <div className="mt-2 mb-4 lg:flex lg:items-center">
        <div style={{ width: '60%' }}>
          <div className="flex flex-row gap-2">
            <label className="mb-0.5">Fuel Product for Pricing</label>
            <Tooltip
              type="black"
              tooltip={
                <div style={{ maxWidth: 200 }} className="p-2">
                  Select a Fuel-Type Product to use to calculate fuel charges upon inventory return. For example, you
                  can create a Product for 'Diesel', or 'Gasoline', to manage pricing for fuel charges.
                </div>
              }
            >
              <Icon icon="InfoCircle" />
            </Tooltip>
          </div>

          <Select
            onChange={onProductChange}
            options={products}
            getOptionLabel={productsToOptions}
            value={fuelProduct}
            isClearable={true}
            isDisabled={isLoading}
            isOptionSelected={(option) => option?.id === fuelProduct?.id}
          />
          {!validFuelProduct && (
            <Typography type="text-sm" className="text-warning-600">
              Selected Fuel Product does not have a Sale Price, please add a Sale Price to the Product.
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};
